import { selectTakes } from "../../../app/slices/TrainingSlice";
import { OnboardingTarget } from "../onboardingTargets";
import type {
  OnboardingStepDefinition,
  OnboardingTour,
} from "../onboardingTypes";

/**
 * The 360 orientation tour.
 *
 * One eight-step sequence, delivered in two stages because exactly one anchor
 * is state-dependent: the Add Interaction CTA only exists once a Bubble is
 * selected (Take360InteractionColumn renders an empty state otherwise). The
 * Bubble column and the Add Bubble CTA are always rendered, so they belong in
 * both stages.
 *
 *   empty project  -> stage A: the two Bubble steps
 *   has takes      -> stage B: all eight, resuming past the Bubble steps only if
 *                     stage A was actually seen
 *
 * Copy is deliberately state-neutral so the shared steps read correctly
 * whether or not the project already has takes.
 *
 * See docs/ONBOARDING_REDESIGN_PLAN.md §3.3.
 */

const hasTakes = (state: any) => (selectTakes(state) ?? []).length > 0;
const completedStageA = (state: any) =>
  state?.onboarding?.completedTours?.includes("editor360StageA") ?? false;

const K = "onboarding.tour.editor360";

const step = (
  target: OnboardingStepDefinition["target"],
  name: string,
  placement: OnboardingStepDefinition["placement"],
): OnboardingStepDefinition => ({
  target,
  titleKey: `${K}.${name}.title`,
  bodyKey: `${K}.${name}.body`,
  placement,
});

/** Available whatever the project state — these Bubble controls always render. */
const takeSteps: OnboardingStepDefinition[] = [
  step(OnboardingTarget.Editor360TakesColumn, "takes", "right"),
  step(OnboardingTarget.Editor360AddTake, "add_take", "top"),
];

/** Needs a selected Bubble: the Add Interaction CTA does not exist without one. */
const authoringSteps: OnboardingStepDefinition[] = [
  step(OnboardingTarget.Editor360AddInteraction, "add_interaction", "right"),
  step(OnboardingTarget.EditorPropertiesColumn, "properties", "left"),
  // Translations sit behind an icon in the toolbar with no label of its own.
  // Nothing else in the product points at it, so the tour has to.
  step(OnboardingTarget.ActionbarTranslate, "translate", "bottom"),
  step(OnboardingTarget.Editor360SaveButton, "save", "bottom"),
  step(OnboardingTarget.ActionbarTestButton, "test", "bottom"),
  step(OnboardingTarget.Editor360PublishButton, "publish", "right"),
];

/** Empty project: only the Bubble steps can resolve. */
export const editor360StageA: OnboardingTour = {
  id: "editor360StageA",
  canRun: (state) => !hasTakes(state),
  steps: takeSteps,
  progressSegment: () => ({
    startIndex: 0,
    labelKey: `${K}.progress.setup`,
  }),
};

/**
 * Has Bubbles: the whole tour.
 *
 * An author who opens an existing training has never seen the Bubble steps, so
 * they get all eight. Someone who just finished stage A skips straight to the
 * authoring steps rather than repeating two they saw a moment ago.
 */
export const editor360StageB: OnboardingTour = {
  id: "editor360StageB",
  canRun: hasTakes,
  steps: [...takeSteps, ...authoringSteps],
  initialStepIndex: (state) => (completedStageA(state) ? takeSteps.length : 0),
  progressSegment: (state) =>
    completedStageA(state)
      ? {
          startIndex: takeSteps.length,
          labelKey: `${K}.progress.authoring`,
        }
      : {
          startIndex: 0,
          labelKey: `${K}.progress.editor`,
        },
};

export const editor360Tours: OnboardingTour[] = [
  editor360StageA,
  editor360StageB,
];
